import React, {
  FC, useContext, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Polygon } from 'react-leaflet';
import { DomEvent } from 'leaflet';
import { getDistance } from 'geolib';
import { Icon } from '@mdi/react';
import {
  mdiAlert, mdiChevronDown, mdiChevronUp, mdiDomain,
} from '@mdi/js';
import { Responder, ResponderInfoRef } from '@x-guard/xgac-types/xgac';
import colors from 'style/colors.module.scss';
import { noop, useLocale } from 'lib';
import { ContactType } from 'types';
import { alarmIdSelector, currentPositionSelector } from 'features/details';
import { alarmContext } from '../../alarmContext';
import { ContactChip } from '../../response/contactChip';
import styles from './style.module.scss';
import { SETTLE_RADIUS_M, useSettledPosition } from './useSettledPosition';
import { useIsMoving } from './useIsMoving';
import { BuildingContact as BuildingContactType, useBuildingContact } from './useBuildingContact';
import { InfoModal } from './infoModal';
import { useCanLookupBuilding } from './useCanLookupBuilding';

// Same contact flow as the response list (modal, call instructions, result survey, logging). There is no asset behind a
// building, so the id stays empty: the log entry gets no target asset.
const toResponder = ({ name, phoneNumber }: BuildingContactType): Responder => ({
  info: {
    _id: '', _ref: ResponderInfoRef.StaticResponder, name, image: null, position: null, locale: null, contactCode: null,
  },
  distanceKm: null,
  actions: {
    call: phoneNumber, sms: null, email: null, notification: false,
  },
  priority: 0,
});

/**
 * Footprint of the building at the current position, with a contact option for it (alarm center only),
 * and a warning when the alarm creator is moving. Render with a key per alarm, so state doesn't carry over.
 */
export const BuildingContact: FC<{ visible: boolean }> = ({ visible }) => {

  const { t } = useLocale();

  const alarmId = useSelector(alarmIdSelector);
  const position = useSelector(currentPositionSelector);

  // the "no valid position" placeholder sits at 0,0
  const [lng, lat] = position?.coordinates && (position.coordinates[0] !== 0 || position.coordinates[1] !== 0) ? position.coordinates : [];

  const moving = useIsMoving(position?.properties?.speed, lng, lat);
  const settled = useSettledPosition(lng, lat);

  // switched off with the map control: no lookup (billed) and nothing drawn
  const lookup = useCanLookupBuilding() && visible;
  const building = useBuildingContact(alarmId, settled, lookup);
  const { onClickContact } = useContext(alarmContext);
  const [showOthers, setShowOthers] = useState(false);
  const [primary, ...others] = building?.contacts ?? [];

  // the card sits on the map: without this the wheel (list scroll) zooms the map and a double click zooms in
  const layoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {

    if (!layoutRef.current) return;

    DomEvent.disableScrollPropagation(layoutRef.current);
    DomEvent.disableClickPropagation(layoutRef.current);

  }, []);

  // the person left the settled spot: hide the building until the next spot has settled
  const away = !!settled && lng !== undefined && lat !== undefined && getDistance(settled, [lng, lat]) > SETTLE_RADIUS_M;
  const showBuilding = lookup && !moving && !away;

  // an automatically found contact is explained first, the regular contact flow starts after the operator continues
  const [pending, setPending] = useState<{ contact: BuildingContactType; contactType: ContactType } | null>(null);

  const contactChip = (contact: BuildingContactType) => (
    <ContactChip
      contactType={null}
      size="small"
      onClick={(contactType) => setPending({ contact, contactType })}
    />
  );

  return (
    <>
      {showBuilding && building && (
        <Polygon
          positions={building.rings.map((ring) => [ring.map(([ringLng, ringLat]) => [ringLat, ringLng] as [number, number])])}
          pathOptions={{ color: colors.primary, weight: 2, fillOpacity: 0.15 }}
        />
      )}
      <div ref={layoutRef} className={styles.layout}>
        {moving && (
          <div className={styles.moving}>
            <Icon path={mdiAlert} size="20px"/>
            <span>{t('Alarm_Detail_Map.Moving')}</span>
          </div>
        )}
        {showBuilding && primary && (
          <div className={styles.contact}>
            <div className={styles.heading}>
              <Icon path={mdiDomain} size="14px"/>
              <span className={styles.title}>
                {t(primary.inBuilding ? 'Alarm_Detail_Map.BuildingContact.Title' : 'Alarm_Detail_Map.BuildingContact.Nearby')}
              </span>
            </div>
            <div className={styles.main}>
              <div className={styles.info}>
                <div className={styles.name}>{primary.name}</div>
                {primary.address && <div className={styles.address}>{primary.address}</div>}
              </div>
              {contactChip(primary)}
            </div>
            {others.length > 0 && (
              <>
                <button type="button" className={styles.toggle} onClick={() => setShowOthers((shown) => !shown)}>
                  <span>{t('Alarm_Detail_Map.BuildingContact.Others', { count: others.length })}</span>
                  <Icon path={showOthers ? mdiChevronUp : mdiChevronDown} size="18px"/>
                </button>
                {showOthers && (
                  <ul className={styles.others}>
                    {others.map((other) => (
                      <li key={`${other.name}-${other.phoneNumber}`} className={styles.other}>
                        <div className={styles.otherInfo}>
                          <div className={styles.otherName}>{other.name}</div>
                          {settled && (
                            <div className={styles.address}>
                              {`${Math.round(getDistance(settled, [other.location.lng, other.location.lat]) / 10) * 10} m`}
                            </div>
                          )}
                        </div>
                        {contactChip(other)}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {pending && (
        <InfoModal
          selected={pending.contact}
          options={(building?.contacts ?? []).map((contact) => ({
            contact,
            distanceM: settled ? Math.round(getDistance(settled, [contact.location.lng, contact.location.lat]) / 10) * 10 : null,
          }))}
          onSelect={(contact) => setPending({ ...pending, contact })}
          onCancel={() => setPending(null)}
          onConfirm={() => {

            onClickContact({
              responder: toResponder(pending.contact),
              contactType: pending.contactType,
              onContactConfirmation: noop,
            });
            setPending(null);

          }}
        />
      )}
    </>
  );

};
